import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, AlertCircle, RefreshCw } from 'lucide-react';
import api from '../services/api';
import LoadingState, { Spinner } from './common/LoadingState';
import { socketService } from '../services/socketService';

interface ImportJobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalUsers: number;
  processedUsers: number;
  emailsSent: number;
  emailsFailed: number;
  progress: number;
  errors: string[];
  startedAt?: string;
  completedAt?: string;
  fileName?: string;
}

interface ImportProgressModalProps {
  importJobId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ImportProgressModal: React.FC<ImportProgressModalProps> = ({
  importJobId,
  isOpen,
  onClose,
}) => {
  const [status, setStatus] = useState<ImportJobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  // Ref para evitar actualizaciones obsoletas de closures
  const importJobIdRef = useRef(importJobId);
  importJobIdRef.current = importJobId;

  // Fallback polling: solo si socket no está conectado
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const bootstrapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasReceivedRealtimeRef = useRef(false);
  const [isPolling, setIsPolling] = useState(false);

  // Carga estado desde el servidor y retorna el estado actual para decisiones de control
  const fetchStatus = useCallback(async (jobId: string): Promise<ImportJobStatus | null> => {
    try {
      const response = await api.api.get(`/users/import/${jobId}`);
      const jobStatus = (response as any).data?.data as ImportJobStatus | undefined;
      if (jobStatus) {
        setStatus(jobStatus);
        setLoading(false);
        setError(null);
        return jobStatus;
      }
      return null;
    } catch (err: any) {
      console.error('[ImportProgressModal] Error cargando estado inicial:', err?.response?.status, err?.message);
      return null;
    }
  }, []);


  useEffect(() => {
    if (!isOpen || !importJobId) {
      setStatus(null);
      setLoading(true);
      setError(null);
      // Limpiar polling si se cierra el modal
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (bootstrapTimeoutRef.current) {
        clearTimeout(bootstrapTimeoutRef.current);
        bootstrapTimeoutRef.current = null;
      }
      setIsPolling(false);
      return;
    }
  hasReceivedRealtimeRef.current = false;


    setLoading(true);
    setError(null);

    // 1. Asegurar conexión Socket.IO
    if (!socketService.isConnected()) {
      socketService.connect();
    }

    // 2. Escuchar progreso en tiempo real (WebSocket)
    const handleProgress = (data: any) => {
      if (data.importJobId !== importJobIdRef.current) return;
      hasReceivedRealtimeRef.current = true;
      setStatus(prev => ({ ...(prev as ImportJobStatus), ...data }));
      setLoading(false);
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
        stopPolling();
      }
    };

    const handleCompleted = (data: any) => {
      if (data.importJobId !== importJobIdRef.current) return;
      // Recargar estado final desde BD para garantizar datos exactos
      fetchStatus(importJobIdRef.current).then((latestStatus) => {
        if (latestStatus && (latestStatus.status === 'completed' || latestStatus.status === 'failed' || latestStatus.status === 'cancelled')) {
          stopPolling();
        }
      });
    };

    socketService.onImportProgress(handleProgress);
    socketService.onImportCompleted(handleCompleted);

    // 3. Fallback polling adaptativo SOLO si socket no está conectado
    function startPolling() {
      if (pollingIntervalRef.current) return;
      setIsPolling(true);
      pollingIntervalRef.current = setInterval(() => {
        fetchStatus(importJobIdRef.current).then((latestStatus) => {
          if (latestStatus && (latestStatus.status === 'completed' || latestStatus.status === 'failed' || latestStatus.status === 'cancelled')) {
            stopPolling();
          }
        });
      }, 2500); // 2.5s entre requests
    }
        // Carga inicial vía HTTP para mostrar estado actual
        fetchStatus(importJobId).then(latestStatus => {
          if (!latestStatus) {
            setError('No se pudo cargar el estado de la importación');
            return;
          }

          if (latestStatus.status === 'completed' || latestStatus.status === 'failed' || latestStatus.status === 'cancelled') {
            stopPolling();
          }
        });

    function stopPolling() {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    }

    // Si socket no está conectado al abrir, arrancar polling
    if (!socketService.isConnected()) {
      startPolling();
    } else {
      // Bootstrap anti-race: por si el job termina antes de recibir el primer evento socket
      startPolling();
      bootstrapTimeoutRef.current = setTimeout(() => {
        if (!hasReceivedRealtimeRef.current) {
          // Si no hubo evento realtime en ventana inicial, mantenemos polling activo
          return;
        }
        stopPolling();
      }, 7000);
    }

    // Si el socket se reconecta, mantenemos polling activo hasta recibir realtime real o estado final.
    const handleSocketConnected = () => {
      if (hasReceivedRealtimeRef.current) {
        stopPolling();
      } else {
        startPolling();
      }
    };
    // Si el socket se desconecta, arrancar polling
    const handleSocketDisconnected = () => {
      startPolling();
    };
    socketService.on('socket:connected', handleSocketConnected);
    socketService.on('socket:disconnected', handleSocketDisconnected);

    return () => {
      socketService.removeImportListeners();
      socketService.off('socket:connected', handleSocketConnected);
      socketService.off('socket:disconnected', handleSocketDisconnected);
      if (bootstrapTimeoutRef.current) {
        clearTimeout(bootstrapTimeoutRef.current);
        bootstrapTimeoutRef.current = null;
      }
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importJobId, isOpen]);

  const handleManualRefresh = async () => {
    setManualRefreshing(true);
    await fetchStatus(importJobId);
    setManualRefreshing(false);
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center py-12">
            <LoadingState layout="section" size="lg" message="Cargando estado..." />
            {isPolling && (
              <p className="text-xs text-blue-400 mt-2">(Modo fallback: polling)</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!status || error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-gray-900 font-semibold mb-2">Error</p>
            <p className="text-gray-600 text-sm mb-6">{error || 'No se encontró la importación'}</p>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = status.status === 'completed' || status.status === 'failed';
  const isFailed = status.status === 'failed';
  const progressPercentage = status.totalUsers > 0 
    ? Math.round((status.processedUsers / status.totalUsers) * 100) 
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4 !mt-0">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Detalles de Importación</h2>
          <div>
            {!isCompleted && <Spinner size="sm" className="text-blue-600" />}
            {isFailed && <AlertCircle className="w-5 h-5 text-red-600" />}
            {isCompleted && !isFailed && <Check className="w-5 h-5 text-green-600" />}
          </div>
        </div>

        {/* File name */}
        {status.fileName && (
          <p className="text-sm text-gray-600 mb-4 truncate">
            <span className="font-semibold">Archivo:</span> {status.fileName}
          </p>
        )}

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progreso</span>
            <span className="font-semibold">{progressPercentage}% ({status.processedUsers}/{status.totalUsers})</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                isFailed ? 'bg-red-600' : isCompleted ? 'bg-green-600' : 'bg-blue-600'
              }`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Stats - Compacto */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Procesados</p>
            <p className="text-lg font-bold text-gray-900">{status.processedUsers}/{status.totalUsers}</p>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-xs text-green-600 uppercase tracking-wide">Enviados</p>
            <p className="text-lg font-bold text-green-600">{status.emailsSent}</p>
          </div>
          {status.emailsFailed > 0 && (
            <div className="bg-red-50 p-3 rounded-lg col-span-2">
              <p className="text-xs text-red-600 uppercase tracking-wide">Fallidos</p>
              <p className="text-lg font-bold text-red-600">{status.emailsFailed}</p>
            </div>
          )}
        </div>

        {/* Status Message */}
        {isCompleted && (
          <div
            className={`p-4 rounded-lg mb-6 flex items-start gap-3 ${
              isFailed ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}
          >
            {isFailed ? (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`font-semibold text-sm ${isFailed ? 'text-red-900' : 'text-green-900'}`}>
                {isFailed ? 'Completada con Errores' : 'Completada Exitosamente'}
              </p>
              <p className={`text-xs mt-1 ${isFailed ? 'text-red-700' : 'text-green-700'}`}>
                {status.emailsSent} email{status.emailsSent !== 1 ? 's' : ''} enviado{status.emailsSent !== 1 ? 's' : ''}.
                {status.emailsFailed > 0 && ` ${status.emailsFailed} fallo${status.emailsFailed !== 1 ? 's' : ''}.`}
              </p>
            </div>
          </div>
        )}

        {/* In Progress Message */}
        {!isCompleted && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
            <p className="text-xs text-blue-700 flex items-center gap-2">
              <Spinner size="sm" />
              <span>
                {status.emailsSent === 0 && status.status === 'pending' && 'En cola, esperando procesamiento...'}
                {status.emailsSent === 0 && status.status === 'processing' && 'Procesando registros...'}
                {status.emailsSent > 0 && `Enviados: ${status.emailsSent} de ${status.totalUsers}`}
                {status.emailsFailed > 0 && ` | Fallidos: ${status.emailsFailed}`}
              </span>
            </p>
            <p className="text-xs text-blue-500 mt-2">
              Recibirás la actualización automáticamente al finalizar.
            </p>
          </div>
        )}

        {/* Errors list - Solo si hay y está completado */}
        {status.errors.length > 0 && isCompleted && (
          <div className="mb-6 max-h-40 overflow-y-auto">
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Errores ({status.errors.length}):
            </p>
            <ul className="space-y-1">
              {status.errors.slice(0, 8).map((error, idx) => (
                <li key={idx} className="text-xs text-red-600">
                  • {error}
                </li>
              ))}
              {status.errors.length > 8 && (
                <li className="text-xs text-gray-500 italic mt-2">
                  +{status.errors.length - 8} errores más...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {!isCompleted && (
            <button
              onClick={handleManualRefresh}
              disabled={manualRefreshing}
              className="px-4 py-2 rounded-lg font-semibold transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
              title="Actualizar estado manualmente"
            >
              <RefreshCw className={`w-4 h-4 ${manualRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg font-semibold transition-colors bg-blue-600 text-white hover:bg-blue-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
